import { useEffect, useMemo, useState } from "react";
import { images } from "@img/imagesMap";
import * as mutate from "@script/mutate";
import "@css/comp_table.css";
import "@css/temp_headNurse_management.css";
import MgmtExcelUpload from "@components/feature/head_nurse_mgmt/MgmtExcelUpload";
import AddGroupModal from "@components/feature/head_nurse_mgmt/AddGroupModal";
import MgmtExcelUpload2 from "@components/feature/head_nurse_mgmt/MgmtExcelUpload2";
import GradeSettingModal from "@components/feature/head_nurse_mgmt/GradeSettingModal";
import useQueryInvaildate from "@hooks/useQueryInvaildate";
import { QUERY_BASE_KEY } from "@constants/query-key";
import type { NurseResponse } from "@vTypes/nurse-type";
import useStore from "@store/store";
import { useGroupSelection } from "@hooks/useGroupSelection";
import useNurses from "@hooks/useNurses";
import ActionButton from "@components/ui/button/btn-action.tsx";
import NurseEditSideProfile from "@components/feature/head_nurse_mgmt/NurseEditSideProfile";
import { toast } from "@/components/toast/CustomToast";

import {
  useTeamsQuery,
  type TeamsSchema,
} from "@/hooks/roster-craete/useTeamsQuery";
import { usePreceptorMapping } from "@/hooks/usePreceptorMapping";
import NurseManagementTables from "./components/NurseManagementTables";
import NurseActiveSwitch from "@/components/feature/head_nurse_mgmt/NurseActiveSwitch";
import NurseAddModal from "@/components/feature/head_nurse_mgmt/NurseAddModal";
import TeamSettingModal from "@/components/feature/head_nurse_mgmt/TeamSettingModal";
import { useSortableNurses } from "@/hooks/useSortableNurses";
import { linkPreceptorRelations } from "@/utils/nurse-util";
import { sortNurseListByRoleThenSequence } from "@/utils/common-util";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ViewModeSelector from "@/components/ui/ViewModeSelector";

function Head_nurse_management() {
  const { groups, fetchGroups, selectedGroupId, setSelectedGroupId, isAdmin } =
    useGroupSelection();
  const { invalidate } = useQueryInvaildate();
  const { loginUser } = useStore();
  const loginUserIsMasterAdmin = loginUser?.is_master_admin; // 어드민만

  const isNoGroupAdmin = useMemo(
    () => isAdmin && Array.isArray(groups) && groups.length === 0,
    [isAdmin, groups]
  );

  const targetGroupId = isAdmin
    ? selectedGroupId || undefined
    : undefined;

  // 상위에서 캐싱해둬서 그냥 넣으면됨
  const { data: nurseDatas } = useNurses(selectedGroupId);
  const { data: teamsSchema } = useTeamsQuery(targetGroupId ?? null);

  const nursesForSorting = useMemo(
    () =>
      linkPreceptorRelations(sortNurseListByRoleThenSequence(nurseDatas ?? [])),
    [nurseDatas]
  );

  // 정렬 상태를 하나로 통합 관리 (활성/비활성 공통)
  const { sortConfig, handleSort } = useSortableNurses(nursesForSorting, {
    enableSequenceUpdate: true, // 활성 근무자는 서버에 저장
    keepRoleGroupOrder: true, // RN → AN → ETC 그룹을 유지한 채 내부 정렬
  });

  // 낙관적 업데이트를 위한 로컬 상태 (부모로 끌어올림)
  const [localTeams, setLocalTeams] = useState<TeamsSchema>(teamsSchema!);

  // 서버 데이터 동기화
  useEffect(() => {
    if (teamsSchema) setLocalTeams(teamsSchema);
  }, [teamsSchema]);

  const [isTeamModalOpen, setIsTeamModalOpen] = useState<boolean>(false);
  const [isNurseModalOpen, setIsNurseModalOpen] = useState<boolean>(false);
  const [isNurseAddModalOpen, setIsNurseAddModalOpen] =
    useState<boolean>(false);
  const [viewState, setViewState] = useState<"ALL" | "TEAM">("ALL");

  const [activeNurseSchema, setActiveNurseSchema] = useState<NurseResponse[]>(
    []
  );

  const [inactiveNurseSchema, setInactiveNurseSchema] = useState<
    NurseResponse[]
  >([]);

  const [isOpendExcelUpload, setIsOpendExcelUpload] = useState<boolean>(false);
  const [isOpendExcelUpload2, setIsOpendExcelUpload2] =
    useState<boolean>(false);

  const [isDeleteAllNursesButtonClick, setIsDeleteAllNursesButtonClick] =
    useState<boolean>(false);

  const [openAddGroup, setOpenAddGroup] = useState(false);

  const [isSideProfileOpend, setIsSideProfileOpend] = useState<boolean>(false);
  const [activeNurseIdForSideProfile, setActiveNurseIdForSideProfile] =
    useState<string | null>(null);
  const currentMgmtGroupId = isAdmin
    ? (selectedGroupId ?? null)
    : (loginUser?.group_id ?? null);
  const activeNurseForSideProfile = useMemo(
    () =>
      (nurseDatas ?? []).find((n) => n.nurse_id === activeNurseIdForSideProfile) ??
      null,
    [nurseDatas, activeNurseIdForSideProfile]
  );
  const isExternalNurseForSideProfile = useMemo(() => {
    if (!activeNurseForSideProfile || !currentMgmtGroupId) return false;
    return activeNurseForSideProfile.group_id !== currentMgmtGroupId;
  }, [activeNurseForSideProfile, currentMgmtGroupId]);

  // 현재팀 활성화 (ID로 관리)
  const [currentTeamId, setCurrentTeamId] = useState<number | null>(null);

  /* //근무자 그룹 내 팀 */
  const [isAddNewNurse, setIsAddNewNurse] = useState<boolean>(false);

  // Grade 설정 모달 상태
  const [isGradeModalOpen, setIsGradeModalOpen] = useState<boolean>(false);

  // 인스턴스 초기화
  const fetchNurseSchema = async (groupId?: string | null) => {
    const targetGroup = (groupId || selectedGroupId) ?? null;
    await invalidate([QUERY_BASE_KEY.NURSES, targetGroup ?? ""]);
    await invalidate(["nurses", "fullSchema"]);
  };

  const { preceptorList } = usePreceptorMapping(nurseDatas!);

  async function deleteAllNurses() {
    if (confirm(`근무자를 전부 삭제하시겠습니까? (본인 제외)`)) {
      setIsDeleteAllNursesButtonClick(true);
      const deleteArray = nurseDatas!.filter(
        (n) => n.nurse_id === loginUser!.nurse_id
      );
      try {
        await mutate.saveChangeFullNurseSchema(
          deleteArray,
          isAdmin ? selectedGroupId : undefined,
          loginUser?.office_id ?? null
        );
        alert("삭제되었습니다.");
        fetchNurseSchema(isAdmin ? (selectedGroupId ?? undefined) : undefined);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "근무자 삭제가 실패했습니다."
        );
      } finally {
        setIsDeleteAllNursesButtonClick(false);
      }
    }
  }

  // SideProfile On off
  function selectIdForSideprofile(bool: boolean, nurseID: string) {
    setActiveNurseIdForSideProfile(bool ? null : nurseID);
    setIsSideProfileOpend(!bool);
  }

  useEffect(() => {
    function filterActive() {
      const activeList: NurseResponse[] = [];
      const inactiveList: NurseResponse[] = [];

      nurseDatas!.forEach((nurse) => {
        if (nurse.active === 1) {
          activeList.push(nurse);
        } else if (nurse.active === 0) {
          inactiveList.push(nurse);
        }
      });

      setActiveNurseSchema(activeList);
      setInactiveNurseSchema(inactiveList);
    }

    filterActive();

    // 무한랜더로 eslint 우선제외
  }, [nurseDatas]);

  return (
    <div className="pb-20 w-full ">
      <div className="flex flex-col gap-2 items-center px-10 w-full">
        <div className="flex flex-col ">
          <div className="flex justify-between">
            <h2 className="font-pretendard text-[24px] font-bold text-sig  leading-relaxed">
              근무자 관리
            </h2>
          </div>

          <div className="flex justify-between mb-6 mt-5 items-center">
            {/* 전체인원 */}
            <div className="flex items-center gap-3.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <img
                        src={images.icon_person_black}
                        alt=""
                        className="mr-[3px]"
                      />
                      <span className="text-sm mt-1 font-semibold">
                        {nurseDatas?.length ?? 0}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      활성 {activeNurseSchema?.length ?? 0}명 / 비활성{" "}
                      {inactiveNurseSchema?.length ?? 0}명
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* 전체 팀별 셀렉터 */}
              <ViewModeSelector
                options={[
                  { value: "ALL", label: "전체보기" },
                  { value: "TEAM", label: "팀별보기" },
                ]}
                defaultValue={viewState}
                onValueChange={(value) => setViewState(value as "TEAM" | "ALL")}
              />
            </div>

            <div className="flex items-center gap-3.5">
              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsNurseAddModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                근무자 추가
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsNurseModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                비활성 근무자 설정
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsGradeModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                Grade 설정
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsTeamModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                팀 설정
              </ActionButton>

              <ActionButton
                icon={"+"}
                disabled={isAddNewNurse || isNoGroupAdmin}
                onClick={() => {
                  setIsOpendExcelUpload(false);
                  setIsOpendExcelUpload2(true);
                }}
              >
                근무자 엑셀 업로드
              </ActionButton>
              {loginUserIsMasterAdmin && (
                <ActionButton
                  icon={"-"}
                  disabled={isDeleteAllNursesButtonClick || isNoGroupAdmin}
                  onClick={deleteAllNurses}
                >
                  전체 삭제
                </ActionButton>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col">
              <NurseManagementTables
                localTeams={localTeams}
                setLocalTeams={setLocalTeams}
                currentTeamId={currentTeamId}
                setCurrentTeamId={setCurrentTeamId}
                selectedGroupId={targetGroupId ?? null}
                activeNurseIdForSideProfile={
                  activeNurseIdForSideProfile ?? null
                }
                selectIdForSideprofile={selectIdForSideprofile}
                showOnlyActive={true}
                isAddNewNurse={isAddNewNurse}
                setIsAddNewNurse={setIsAddNewNurse}
                viewState={viewState}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>

            {/* 비활성 */}
            <div className="flex flex-col">
              <h3 className="px-10 text-lg font-bold text-white bg-[#C4C4C4] p-2 text-center rounded-t-lg grid grid-cols-3">
                <div className="flex items-center gap-2">
                  <img src={images.icon_person} alt="" className="size-3 " />
                  {inactiveNurseSchema?.length ?? 0}
                </div>
                <div>비활성 근무자</div>
                <div></div>
              </h3>
              <NurseManagementTables
                localTeams={localTeams}
                setLocalTeams={setLocalTeams}
                currentTeamId={currentTeamId}
                setCurrentTeamId={setCurrentTeamId}
                selectedGroupId={targetGroupId ?? null}
                activeNurseIdForSideProfile={
                  activeNurseIdForSideProfile ?? null
                }
                selectIdForSideprofile={selectIdForSideprofile}
                showOnlyActive={false}
                viewState={viewState}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 엑셀 업로드 */}
      {preceptorList && isOpendExcelUpload && (
        <MgmtExcelUpload
          fullNurseSchema={nurseDatas}
          preceptorList={preceptorList}
          fetchNurseSchema={fetchNurseSchema}
          setIsOpendExcelUpload={setIsOpendExcelUpload}
        />
      )}

      {/* 사이드 프로필 모달 */}
      {isSideProfileOpend && (
        <NurseEditSideProfile
          isOpen={isSideProfileOpend}
          onClose={() => {
            setIsSideProfileOpend(false);
            setActiveNurseIdForSideProfile(null);
          }}
          selectedGroupId={selectedGroupId}
          isAdmin={isAdmin}
          nurseId={activeNurseIdForSideProfile}
          allNurses={nurseDatas}
          disableShiftAndWantedEdit={isExternalNurseForSideProfile}
        />
      )}

      {/* 그룹 수정 */}
      {openAddGroup && (
        <AddGroupModal
          onClose={() => setOpenAddGroup(false)}
          onCreated={async (g) => {
            setOpenAddGroup(false);
            await fetchGroups();
            setSelectedGroupId(g.group_id);
          }}
          fetchGroups={fetchGroups}
        />
      )}

      {/* 엑셀 업로드 */}
      {isOpendExcelUpload2 && (
        <MgmtExcelUpload2
          onClose={() => setIsOpendExcelUpload2(false)}
          isMasterAdmin={Boolean(loginUser?.is_master_admin)}
          selectedGroupId={selectedGroupId}
          fetchNurseSchema={(gid?: string) => fetchNurseSchema(gid)}
        />
      )}

      {/* Grade 설정 */}
      {isGradeModalOpen && (
        <GradeSettingModal
          onClose={() => setIsGradeModalOpen(false)}
          isAdmin={isAdmin}
          selectedGroupId={selectedGroupId}
        />
      )}

      {/* 팀설정 */}
      {isTeamModalOpen && (
        <TeamSettingModal
          onClose={() => setIsTeamModalOpen(false)}
          selectedGroupId={isAdmin ? selectedGroupId : ""}
        />
      )}

      {/* 근무자 활성 */}
      {isNurseModalOpen && (
        <NurseActiveSwitch
          onClose={() => setIsNurseModalOpen(false)}
          selectedGroupId={selectedGroupId}
        />
      )}

      {/* 근무자 추가 */}
      {isNurseAddModalOpen && (
        <NurseAddModal
          onClose={() => setIsNurseAddModalOpen(false)}
          selectedGroupId={selectedGroupId}
        />
      )}
    </div>
  );
}
export default Head_nurse_management;
